// @tennis-matches/services/governance
// Zusammenfassung: eigene ausstehende Zugriffs-/Registrierungsanfragen (keine Admin-Queries).

import { getSupabaseClient } from "../supabase/index.js";
import { loadMyAccessRequestsRemote } from "./access-requests.service.js";
import { loadMyPendingRegistrationOnboardingRemote } from "./registration-onboarding.service.js";
import { loadMyRoleBindingsRemote } from "./role-bindings.service.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __isSelfPendingAccessRequest(row, uid) {
  const st = String(row?.status || "").toLowerCase();
  if (st !== "pending") return false;
  return __asStr(row?.requester_user_id) === uid && __asStr(row?.target_user_id) === uid;
}

function __hasTerminalSelfAccessDecision(allAccess, uid) {
  for (const r of allAccess) {
    if (__asStr(r?.requester_user_id) !== uid || __asStr(r?.target_user_id) !== uid) continue;
    const st = String(r?.status || "").toLowerCase();
    if (st === "approved" || st === "rejected" || st === "cancelled") return true;
  }
  return false;
}

/** Registrierung abgeschlossen (Metadaten), aber noch keine Freigabe/Endentscheidung in access_requests. */
const PENDING_WAIT_CACHE_MS = 45000;

let __pendingWaitInflight = null;
/** @type {string} */
let __pendingWaitInflightUid = "";
/** @type {{ uid: string, ts: number, result: object } | null} */
let __pendingWaitCache = null;

export function invalidateGovernancePendingWaitCache() {
  __pendingWaitCache = null;
  __pendingWaitInflight = null;
  __pendingWaitInflightUid = "";
}

async function __currentAuthUserId() {
  const supabase = await getSupabaseClient();
  if (!supabase) return "";
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user?.id) return "";
  return __asStr(data.user.id);
}

function __selfSignupAppliedAwaitingDecision(meta, allAccess, uid) {
  const pending = meta?.pending_self_access_request;
  if (!pending || typeof pending !== "object" || pending.applied !== true) return false;
  const roles = Array.isArray(pending.requested_roles) ? pending.requested_roles : [];
  if (!roles.length) return false;
  if (__hasTerminalSelfAccessDecision(allAccess, uid)) return false;
  return true;
}

/**
 * Lädt ob der aktuelle Nutzer auf eine Freigabe warten muss (Self-Access-Request pending
 * und/oder eigene registration_onboarding_requests pending).
 * @returns {Promise<{ ok: boolean, shouldWait: boolean, accessPending: any[], registrationPending: any|null, reason?: string }>}
 */
async function __loadGovernancePendingWaitRemoteImpl() {
  const supabase = await getSupabaseClient();
  if (!supabase) {
    return { ok: false, reason: "no-supabase", shouldWait: false, accessPending: [], registrationPending: null };
  }

  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr || !authData?.user?.id) {
    return { ok: false, reason: "no-user", shouldWait: false, accessPending: [], registrationPending: null };
  }
  const uid = __asStr(authData.user.id);

  const [accessRes, regRes, bindingsRes] = await Promise.all([
    loadMyAccessRequestsRemote(),
    loadMyPendingRegistrationOnboardingRemote(),
    loadMyRoleBindingsRemote(),
  ]);

  const allAccess = accessRes?.ok && Array.isArray(accessRes.requests) ? accessRes.requests : [];
  const accessPending = allAccess.filter((r) => __isSelfPendingAccessRequest(r, uid));

  const registrationPending = regRes?.ok && regRes.row ? regRes.row : null;

  const meta =
    authData.user.user_metadata && typeof authData.user.user_metadata === "object"
      ? authData.user.user_metadata
      : {};
  const selfSignupAwaiting = __selfSignupAppliedAwaitingDecision(meta, allAccess, uid);

  // Wenn der User bereits mindestens ein aktives role_binding hat (z. B. durch
  // Auto-Provisioning aus club_people oder durch frühere Freigabe), darf er
  // direkt in den Hub — auch wenn parallel noch eine pending Anfrage existiert
  // (Vereinsadmin kann diese später nachträglich entscheiden).
  const bindings = bindingsRes?.ok && Array.isArray(bindingsRes.bindings) ? bindingsRes.bindings : [];
  const hasActiveBinding = bindings.some((b) => b && (b.is_active === undefined || b.is_active === null || b.is_active === true));

  const shouldWait =
    !hasActiveBinding && (accessPending.length > 0 || Boolean(registrationPending) || selfSignupAwaiting);

  return {
    ok: true,
    shouldWait,
    accessPending,
    registrationPending,
    hasActiveBinding,
  };
}

/** Kurzzeit-Cache + Singleflight: reactApp + AccessPendingPage teilen eine Antwort (pro User-ID). */
export async function loadGovernancePendingWaitRemote(options = {}) {
  const force = Boolean(options?.force);
  const now = Date.now();
  const uid = await __currentAuthUserId();

  if (
    !force &&
    uid &&
    __pendingWaitCache &&
    __pendingWaitCache.uid === uid &&
    now - __pendingWaitCache.ts < PENDING_WAIT_CACHE_MS
  ) {
    return __pendingWaitCache.result;
  }

  if (__pendingWaitInflight && __pendingWaitInflightUid === uid) {
    return __pendingWaitInflight;
  }

  if (__pendingWaitInflight && __pendingWaitInflightUid && __pendingWaitInflightUid !== uid) {
    invalidateGovernancePendingWaitCache();
  }

  const promise = __loadGovernancePendingWaitRemoteImpl();
  __pendingWaitInflight = promise;
  __pendingWaitInflightUid = uid;
  try {
    const result = await promise;
    if (uid) {
      __pendingWaitCache = { uid, ts: Date.now(), result };
    }
    return result;
  } finally {
    if (__pendingWaitInflight === promise) {
      __pendingWaitInflight = null;
      __pendingWaitInflightUid = "";
    }
  }
}
